import React, { useMemo, useState } from "react";
import {
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { useNotify } from "react-admin";

import { isAuthorized } from "../../../utils/appUtils";
import {
  AclType,
  InventoryStatus,
  originDamageList,
} from "../../../utils/constants";
import { handleNumberFieldKeyDown } from "../../../utils/utils";

import { useWorkOrderContext } from "./context";
import { BarcodeScanDialog } from "./BarcodeScanDialog";
import { handleScanButtonClick } from "./ScannerUtlis";
import { useZoneSelector } from "./useZoneSelector";

export const UnstructuredAisleControl = ({
  handleDeleteButtonClick,
  handleAisleChange,
  handleSave,
  classes,
  allBarcodes = [],
  setAllBarcodes,
  pendingBarcodes = [],
}) => {
  const { state, dispatch } = useWorkOrderContext();
  const {
    currentInventory,
    aisles,
    selectedAisle,
    selectedLocation,
    selectedInventoryItem,
    disableSelection,
  } = state;
  const isContainerDrop = !!currentInventory?.containerNumber;
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const notify = useNotify();
  const { zones, selectedZone, showZoneSelect, handleZoneChange } =
    useZoneSelector();
  const isQuantityAdded =
    selectedInventoryItem?.quantityOnHand > 0 &&
    selectedInventoryItem?.barcodes?.items?.length === 0;

  // Check if the item is unloaded by the current user
  const isOwnedItem = !selectedInventoryItem.unloadedBy
    ? true
    : window.appUser?.fullName === selectedInventoryItem.unloadedBy;

  const setQuantity = (quantity) => {
    const newInventoryItem = {
      ...selectedInventoryItem,
      quantityOnHand: quantity,
    };
    dispatch({
      type: "SET_SELECTED_INVENTORY_ITEM",
      payload: newInventoryItem,
    });
  };

  const getQuantity = () => {
    return selectedInventoryItem?.quantityOnHand ?? "";
  };

  const isAisleDisabled = () => {
    return disableSelection;
  };

  const handleDelete = () => {
    handleDeleteButtonClick();
  };

  const isDeleteButtonDisabled = () => {
    if (
      !isAuthorized(AclType.ASSIGN_LOCATION) ||
      !selectedLocation ||
      !selectedInventoryItem?.id ||
      currentInventory.status === InventoryStatus.UNLOADED ||
      !isOwnedItem
    )
      return true;
    return false;
  };

  const isScanButtonDisabled = () => {
    if (
      !isOwnedItem ||
      !selectedAisle ||
      !selectedLocation ||
      isQuantityAdded
    ) {
      return true;
    }
    return false;
  };
  const isSaveButtonDisabled = useMemo(() => {
    debugger;
    if (
      !selectedAisle ||
      !selectedLocation ||
      !selectedInventoryItem?.quantityOnHand ||
      !isOwnedItem
    ) {
      return true;
    }

    if (selectedInventoryItem?.status === InventoryStatus.IN_TRANSIT) {
      // item is allocated to location, cannot change location, must be able to place
      return false;
    }
    // selectedInventoryItem?.status !== InventoryStatus.IN_TRANSIT &&
    if (!isAuthorized(AclType.ASSIGN_LOCATION)) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, selectedInventoryItem?.quantityOnHand]);

  const isQuantityDisabled = () => {
    if (
      !selectedAisle ||
      !isAuthorized(AclType.ASSIGN_LOCATION) ||
      currentInventory.status === InventoryStatus.UNLOADED ||
      allBarcodes.length > 0 ||
      selectedInventoryItem?.barcodes?.items?.length > 0
    ) {
      return true;
    }
    return false;
  };

  const setIsDamaged = (value) => {
    const inventoryItem = selectedInventoryItem
      ? { ...selectedInventoryItem }
      : {};
    if (value) {
      inventoryItem.isDamaged = true;
      inventoryItem.originOfDamage = value;
      inventoryItem.hasException = true;
    } else {
      inventoryItem.isDamaged = false;
      inventoryItem.originOfDamage = "";
      inventoryItem.hasException = false;
    }
    dispatch({ type: "SET_SELECTED_INVENTORY_ITEM", payload: inventoryItem });
  };

  const handleScanButton = () => {
    handleScanButtonClick({
      selectedInventoryItem,
      allBarcodes,
      pendingBarcodes,
      setAllBarcodes,
      setScanDialogOpen,
      notify,
    });
  };

  const handleScanClose = () => {
    setScanDialogOpen(false);
  };

  const handleSaveBarcode = (total) => {
    setQuantity(total);
    setScanDialogOpen(false);
  };

  const inputGridSizes = showZoneSelect
    ? { xs: 6, sm: 6, md: 3, lg: 2 }
    : { xs: 12, sm: 4, md: 4, lg: 3 };
  const buttonGridSizes = isContainerDrop ? { xs: 6, lg: 1 } : { xs: 4, lg: 1 };

  return (
    <Grid item container xs={12} spacing={2}>
      {showZoneSelect && (
        <Grid item {...inputGridSizes} xs={6}>
          <TextField
            id="zone"
            label="Zone"
            variant="outlined"
            select
            size="small"
            disabled={isAisleDisabled()}
            required
            autoFocus
            fullWidth
            value={selectedZone ? selectedZone.id : ""}
            onChange={(evt) => handleZoneChange(evt.target.value)}
          >
            {zones &&
              zones.map((itm) => (
                <MenuItem key={itm.id} value={itm.id}>
                  {itm.name}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
      )}
      <Grid item {...inputGridSizes}>
        <TextField
          id="aisle"
          label="Aisle"
          variant="outlined"
          select
          size="small"
          disabled={isAisleDisabled()}
          required
          autoFocus
          fullWidth
          value={selectedAisle ? selectedAisle.id : ""}
          onChange={(evt) =>
            handleAisleChange(evt.target.value, null, { userInitiated: true })
          }
        >
          {aisles &&
            [{ id: "", value: "", name: <span>&nbsp;</span> }, ...aisles].map(
              (itm) => (
                <MenuItem key={itm.id} value={itm.id}>
                  {itm.name}
                </MenuItem>
              ),
            )}
        </TextField>
      </Grid>
      <Grid item {...inputGridSizes} xs={6}>
        <TextField
          id="quantity-received"
          label="Quantity"
          variant="outlined"
          size="small"
          type="number"
          fullWidth
          inputProps={{ min: 1 }}
          disabled={isQuantityDisabled()}
          value={getQuantity()}
          onChange={(evt) => setQuantity(evt.target.value)}
          onKeyDown={handleNumberFieldKeyDown}
          helperText={
            allBarcodes.length > 0
              ? `Scanned barcodes: ${allBarcodes.length}`
              : false
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {currentInventory.inventoryUOM}
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item {...inputGridSizes} xs={6}>
        <TextField
          id="damage"
          label="Damaged"
          variant="outlined"
          select
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={
            selectedInventoryItem?.originOfDamage
              ? selectedInventoryItem.originOfDamage
              : ""
          }
          onChange={(evt) => setIsDamaged(evt.target.value)}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              if (!value) return "No Damage";
              const matchedDamage = originDamageList.find(
                (item) => item.id === value,
              );
              return matchedDamage?.name ?? value;
            },
          }}
        >
          {[
            { id: "", value: "No Damage", name: <span>No Damage</span> },
            ...originDamageList,
          ].map((itm) => (
            <MenuItem key={itm.id} value={itm.id}>
              {itm.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {!isContainerDrop && (
        <Grid item {...buttonGridSizes}>
          <Button
            fullWidth
            variant="contained"
            className={classes.iconButton}
            disabled={isScanButtonDisabled()}
            onClick={handleScanButton}
          >
            Scan
          </Button>
        </Grid>
      )}
      <Grid item {...buttonGridSizes}>
        <Button
          fullWidth
          variant="contained"
          className={classes.deleteButton}
          disabled={isDeleteButtonDisabled()}
          onClick={handleDelete}
        >
          Remove
        </Button>
      </Grid>
      <Grid item {...buttonGridSizes}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          className={classes.iconButton}
          disabled={isSaveButtonDisabled}
          onClick={handleSave}
        >
          {!selectedInventoryItem?.id ||
          selectedInventoryItem?.status === InventoryStatus.IN_TRANSIT
            ? "place"
            : "save"}
        </Button>
      </Grid>
      {scanDialogOpen && (
        <BarcodeScanDialog
          open={scanDialogOpen}
          onClose={handleScanClose}
          onSave={handleSaveBarcode}
          inventoryItem={selectedInventoryItem}
          currentInventory={currentInventory}
          allBarcodes={allBarcodes}
          setAllBarcodes={setAllBarcodes}
        />
      )}
    </Grid>
  );
};
